/**
 * TypeScript definitions for the user information returned by the backend
 * Based on the UserWithPermissions struct in backend/internal/auth/middleware.go
 */

/**
 * User information from WorkOS
 */
export type WorkOSUser = {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  email_verified: boolean;
  profile_picture_url: string;
  created_at: string;
  updated_at: string;
  last_sign_in_at: string;
};

/**
 * User information with permissions from the backend
 */
export type UserInfo = {
  Permissions: Permission[];
  Role: string;
  OrgID: string;
  organization_domain?: string;
  admin?: {
    name: string;
    email: string;
    profile_picture_url: string;
  };
} & WorkOSUser;

/**
 * Response from the register endpoint
 */
export type RegisterResponse = {
  organization_domain: string;
  admin: {
    name: string;
    email: string;
    profile_picture_url: string;
  };
};

export function hasPermission(user: UserInfo | null, permission: Permission): boolean {
  if (!user?.Permissions) return false;
  return user.Permissions.includes(permission);
}

// isInitialAdmin
export function isAdmin(user: UserInfo | null): boolean {
  return hasPermission(user, 'admin:ssoconfig');
}

export const PERMISSIONS = {
  COMPONENT_VIEW: 'component:view',
  COMPONENT_CREATE: 'component:create',
  COMPONENT_PROMOTE: 'component:promote',
  COMPONENT_EDIT: 'component:edit',
  CAPABILITY_MANAGE: 'capability:manage',
  ADMIN_SSOCONFIG: 'admin:ssoconfig',
  APIKEY_CREATE: 'apikey:create',
  APIKEY_INVALIDATE: 'apikey:invalidate',
  PLAN_CREATE: 'plan:create',
  PLAN_APPLY: 'plan:apply',
  CLUSTER_CREATE: 'cluster:create',
  AUDIT_VIEW: 'audit:view',
  TEAM_MANAGE: 'team:manage',
  LINKS_MANAGE: 'links:manage',
  METRICS_VIEW: 'metrics:view',
  SECRET_CREATE: 'secret:create',
} as const;

export type Permission = (typeof PERMISSIONS)[keyof typeof PERMISSIONS];
